import { get, post, patch, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const quotationTrackingService = {
  /**
   * Get tracking data for a quotation
   * @param {String} quoteId - Quotation ID
   * @returns {Promise<Object>} Tracking data
   */
  getTrackingData: async (quoteId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/tracking`);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve tracking data'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve tracking data'
      };
    }
  },

  /**
   * Resend quotation via email
   * @param {String} quoteId - Quotation ID
   * @param {String} email - Recipient email
   * @param {String} message - Optional message
   * @returns {Promise<Object>} Result
   */
  resendQuote: async (quoteId, email, message) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/resend`, {
        email: email,
        message: message || ''
      });
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to resend quotation'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to resend quotation'
      };
    }
  },

  /**
   * Add reminder
   * @param {String} quoteId - Quotation ID
   * @param {Object} reminderData - Reminder data
   * @returns {Promise<Object>} Created reminder
   */
  addReminder: async (quoteId, reminderData) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/reminders`, reminderData);
      
      if (response && response.responseCode === 201) {
        return {
          success: true,
          reminder: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create reminder'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create reminder'
      };
    }
  },

  /**
   * Update reminder
   * @param {String} quoteId - Quotation ID
   * @param {String} reminderId - Reminder ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated reminder
   */
  updateReminder: async (quoteId, reminderId, updateData) => {
    try {
      const response = await patch(`${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/reminders/${reminderId}`, updateData);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          reminder: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update reminder'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update reminder'
      };
    }
  },

  /**
   * Delete reminder
   * @param {String} quoteId - Quotation ID
   * @param {String} reminderId - Reminder ID
   * @returns {Promise<Object>} Result
   */
  deleteReminder: async (quoteId, reminderId) => {
    try {
      const response = await del(`${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/reminders/${reminderId}`);
      
      if (response && response.responseCode === 200) {
        return {
          success: true
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete reminder'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete reminder'
      };
    }
  },

  /**
   * Export communication log
   * @param {String} quoteId - Quotation ID
   * @returns {Promise<Blob>} JSON file blob
   */
  exportLog: async (quoteId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/export-log`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotation-log-${quoteId}-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export log'
      };
    }
  },

  /**
   * Update quote status
   * @param {String} quoteId - Quotation ID
   * @param {String} status - New status
   * @param {String} orderNumber - Optional order number
   * @param {String} plannedDispatchDate - Optional planned dispatch date
   * @param {String} deliveryType - Optional delivery type
   * @returns {Promise<Object>} Updated quotation
   */
  updateQuoteStatus: async (quoteId, status, orderNumber = null, plannedDispatchDate = null, deliveryType = null) => {
    try {
      const payload = { status };
      if (orderNumber) {
        payload.orderNumber = orderNumber;
      }
      if (plannedDispatchDate) {
        payload.plannedDispatchDate = plannedDispatchDate;
      }
      if (deliveryType) {
        payload.deliveryType = deliveryType;
      }
      const response = await patch(`${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}/status`, payload);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          quotation: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update quote status'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update quote status'
      };
    }
  }
};

export default quotationTrackingService;

